import { useTranslation } from "react-i18next";
import type { AnalyticsWorkerDto } from "@normax-performance/shared-types";
import { Button } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../../lib/format";

export interface AttentionListProps {
  /** Already-filtered/sorted/capped by `selectAttentionWorkers` — this component never re-derives the selection. */
  shown: AnalyticsWorkerDto[];
  totalBelowCount: number;
  isLoading: boolean;
  onViewAll: () => void;
}

/**
 * "Needs attention" (docs/NORMAX_PERFORMANCE_DASHBOARD_SLICE_C_IMPLEMENTATION_REPORT.md
 * §9) — a compact list, NOT a full sortable worker table (Analytics already
 * owns that). Purely presentational: filtering/sorting/capping already
 * happened in `selectAttentionWorkers`. Never mixes non-eligible workers in
 * — `shown` is trusted to already exclude them.
 */
export function AttentionList({ shown, totalBelowCount, isLoading, onViewAll }: AttentionListProps) {
  const { t } = useTranslation("dashboard");
  const { intlLocale } = useLocale();

  if (isLoading) {
    return (
      <div className="nx-dashboard__attention">
        <p className="nx-dashboard__section-title">{t("attention.title")}</p>
        <span className="nx-skeleton" style={{ width: "100%", height: "80px" }} />
      </div>
    );
  }

  return (
    <div className="nx-dashboard__attention">
      <p className="nx-dashboard__section-title">{t("attention.title")}</p>
      {shown.length === 0 ? (
        <p className="nx-dashboard__attention-empty">{t("attention.empty")}</p>
      ) : (
        <div className="nx-analytics-table-wrap">
          <table className="nx-analytics-table nx-table">
            <thead>
              <tr>
                <th>{t("attention.code")}</th>
                <th>{t("attention.worker")}</th>
                <th className="nx-analytics-table__num">{t("attention.performance")}</th>
                <th className="nx-analytics-table__num">{t("attention.normedMinutes")}</th>
                <th className="nx-analytics-table__num">{t("attention.trend")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((w) => (
                <tr key={w.workerId}>
                  <td>{w.employeeCode ?? "—"}</td>
                  <td>{w.displayName}</td>
                  <td className="nx-analytics-table__num">
                    <span className="nx-analytics-table__cell-readout nx-analytics-table__cell-readout--below">
                      {formatAnalyticsPercent(w.performancePercentLabel, intlLocale)}
                    </span>
                  </td>
                  <td className="nx-analytics-table__num">{formatAnalyticsMinutes(w.normedMinutes, intlLocale)}</td>
                  <td
                    className={`nx-analytics-table__num nx-analytics__trend-cell nx-analytics__trend-cell--${w.trend.direction ?? "unavailable"}`}
                  >
                    {formatAnalyticsTrend(w.trend.trendPercentagePoints)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalBelowCount > shown.length ? (
        <Button variant="ghost" size="sm" onClick={onViewAll} className="nx-dashboard__attention-viewall">
          {t("attention.viewAll")}
        </Button>
      ) : null}
    </div>
  );
}
